// Updated TranscriptBodyContainer.jsx - Focuses on data fetching and state management
import React, { useEffect, useCallback, useContext } from 'react';
import EditTranscriptContainer from './EditTranscriptContainer';
import TranscriptPagingUtil from 'utils/TranscriptPagingUtil';
import TranscriptScrollbar from './TranscriptScrollbar';
import UrlUtil from 'utils/UrlUtil';
import CodeableContext from 'contexts/CodeableContext';
import CodeOperationsContext from 'contexts/CodeOperationsContext';
import { EditParagraphProvider } from 'contexts/EditParagraphContext';
import {
  useSelectSnippetEffect,
  useTextSelectionEffect,
  useSearchRedirectEffect
} from './useSelectionEffects';
import useCoders from 'hooks/useCoders';
import { useTranscript } from 'hooks/transcripts/queries';
import { useProjectUsers } from 'hooks/users/queries';
import { useCurrentUser } from 'hooks/users/queries';
import { useCodes } from 'hooks/codes/queries';
import PageLoadingComponent from './PageLoadingComponent';
import useDefaultErrorToast from 'hooks/useDefaultErrorToast';

const queryString = require('query-string');

const TranscriptBodyContainer = (props) => {
  // Use the new React Query hook for transcript data
  const transcriptID = parseInt(props?.match?.params?.transcriptID)
  
  const { 
    data: transcriptData,
    isLoading: isLoadingTranscript,
    error: transcriptError
  } = useTranscript(transcriptID);

  // Get the current user for permissions
  /*
    Note while none of these are used directly in this component,
    they are used in the context and child components.
  */
  const { data: currentUser } = useCurrentUser();
  const { data: projectUsers } = useProjectUsers(props.projectID);
  const {data: codes} = useCodes(props.projectID);
  
  const parsed = queryString.parse(props.location.search);
  const { search, pos, end, snippet } = parsed;
  const page = UrlUtil.getPageNumber(props.location);

  const shouldGotoOtherPage = useCallback((newPage) => {
    if (page !== newPage) {
      props.history.push(`${props.location.pathname}${TranscriptPagingUtil.calcQueryParams(props.location.search, newPage)}`);
      return true;
    }
  }, [props.location.search, page, props.location.pathname]);

  const {
    onSelectText,
    select,
    selectedExcerptId,
    onDeselect
  } = useContext(CodeableContext);

  const {
    onExcerptSelected
  } = useContext(CodeOperationsContext);


  const searchRedirect = useCallback((transcriptID, searchText) => {
    if (!searchText || !transcriptData?.body) return;

    // Find the location of searchText in transcript body
    const body = transcriptData?.body;
    const loc = body.indexOf(searchText);
    
    if (loc >= 0) {
      props.history.replace(`/transcripts/${transcriptID}?pos=${loc}&end=${loc + searchText.length}`);
    }
  }, [props.history, transcriptData?.body]);

  // Selection effects. Clearing the selection when the transcript changes
  // is handled centrally by CodeableContextProvider's route-change effect.
  useSelectSnippetEffect(onExcerptSelected, snippet);
  useTextSelectionEffect(onSelectText, pos, end, transcriptID);
  useSearchRedirectEffect(searchRedirect, transcriptID, search);

  // Trigger search redirect when transcript data loads
  useEffect(() => {
    if (transcriptData && search) {
      searchRedirect(transcriptID, search);
    }
  }, [transcriptData, search, transcriptID, searchRedirect]);

  // Handle coder filtering using our custom hook
  const coders = useCoders(props.location.pathname, props.projectID);

  // Show error toast if transcript loading fails (skip 404/403 — handled by TranscriptNav's NotFoundComponent)
  const isNotFoundOrForbidden = transcriptError?.status === 404 || transcriptError?.status === 403;
  useDefaultErrorToast(isNotFoundOrForbidden ? null : transcriptError);

  // Show loading state
  if (isLoadingTranscript) {
    return <PageLoadingComponent />;
  }

  // If there's an error, return null (toast will show the error)
  if (transcriptError) {
    return null;
  }

  return (
    <EditParagraphProvider transcriptId={transcriptID}>
      <div style={{ height: '100%' }}>
        <EditTranscriptContainer transcriptID={transcriptID} />
        <TranscriptScrollbar
          transcriptID={transcriptID}
          transcriptData={transcriptData}
          coders={coders}
          selectedExcerptId={selectedExcerptId}
          select={select}
          page={page}
          shouldGotoOtherPage={shouldGotoOtherPage}
          hasEditPermission={props.hasEditPermission}
          pageCount={props.pageCount}
          onDeselect={onDeselect}
          snippet={snippet}
          search={search}
          pos={pos}
        />
      </div>
    </EditParagraphProvider>
  );
};

export default TranscriptBodyContainer;